/**
 * Client Repository
 * Handles all client-related API calls
 */

import { apiClient } from '../client'
import type { PaginatedResponse } from './role.repository'
import { Tenant } from './tenant.repository'
import { City } from './universe/city.repository'
import { Country } from './universe/country.repository'

export interface Client {
    id: number
    tenant_id: number
    en_name: string
    ar_name?: string | null
    legal_name: string
    business_model?: string | null
    description?: string | null
    client_code?: string | null
    start_date?: Date | null
    end_date?: Date | null
    industry?: string | null
    client_class?: string | null
    priority_level: number
    country_id?: number | null
    city_id?: number | null
    contact_person?: string | null
    contact_email?: string | null
    contact_phone?: string | null
    preferred_contact_method?: string | null
    website?: string | null
    logo_url?: string | null
    legal_status?: string | null
    registration_number?: string | null
    compliance_notes?: string | null
    assigned_account_manager?: number | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date,
    country?: Country | null
    city?: City | null
    tenant: Tenant;
}

export interface CreateClientRequest {
    tenant_id: number
    en_name: string
    ar_name?: string
    legal_name: string
    business_model?: string
    description?: string
    client_code?: string
    start_date?: Date
    end_date?: Date
    industry?: string
    client_class?: string
    priority_level?: number
    country_id?: number
    city_id?: number
    contact_person?: string
    contact_email?: string
    contact_phone?: string
    preferred_contact_method?: string
    website?: string
    logo_url?: string
    legal_status?: string
    registration_number?: string
    compliance_notes?: string
    assigned_account_manager?: number
    status?: number
}

export interface UpdateClientRequest {
    tenant_id?: number
    en_name?: string
    ar_name?: string
    legal_name?: string
    business_model?: string
    description?: string
    client_code?: string
    start_date?: Date
    end_date?: Date
    industry?: string
    client_class?: string
    priority_level?: number
    country_id?: number
    city_id?: number
    contact_person?: string
    contact_email?: string
    contact_phone?: string
    preferred_contact_method?: string
    website?: string
    logo_url?: string
    legal_status?: string
    registration_number?: string
    compliance_notes?: string
    assigned_account_manager?: number
    status?: number
}

class ClientRepository {
    /**
     * Get all clients with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<Client>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.tenant_id) {
                params.append('tenant_id', filters.tenant_id)
            }
            if (filters.client_id) {
                params.append('client_id', filters.client_id)
            }
        }

        return apiClient.get<PaginatedResponse<Client>>(`/clients?${params.toString()}`)
    }

    /**
     * Get client by ID
     */
    async getById(id: number): Promise<Client> {
        return apiClient.get<Client>(`/clients/${id}`)
    }

    /**
     * Create a new client
     */
    async create(data: CreateClientRequest): Promise<Client> {
        return apiClient.post<Client>('/clients', data)
    }

    /**
     * Update an existing client
     */
    async update(id: number, data: UpdateClientRequest): Promise<Client> {
        return apiClient.put<Client>(`/clients/${id}`, data)
    }

    /**
     * Delete a client
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/clients/${id}`)
    }

    /**
     * Export clients to Excel
     */
    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters) {
            if (filters.search) params.append('search', filters.search)
            if (filters.status) params.append('status', filters.status)
            if (filters.client_id) params.append('client_id', filters.client_id.toString())
        }

        const baseUrl = `/clients/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url)


    }
}

// Export singleton instance
export const clientRepository = new ClientRepository()

